/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MutationTrigger,
  UseMutationStateResult,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react';
import { createSharedState } from '@versita/frontend/react-utils';
import dedent from 'dedent';
import { useEffect } from 'react';

type QueryError =
  | {
      status: string | number;
      endpointName: string;
      requestId: string;
      originalArgs?: any;
    }
  | Error;

type MutationError =
  | {
      status: string | number;
      endpointName: string;
      originalArgs?: any;
    }
  | Error;

export const useQueryError = createSharedState<QueryError | null>();
export const useMutationError = createSharedState<MutationError | null>();
/**
 * Checks for error in a useQuery result and throws an error if it exists
 * See: [here](https://redux-toolkit.js.org/rtk-query/overview) for info about rtk-query
 */
export const useHandleQueryError = <
  T extends TypedUseQueryHookResult<any, any, any, any>,
>(
  queryResult: T,
): T => {
  const [, setQueryError] = useQueryError();
  const response = queryResult as {
    error: unknown;
    endpointName: string;
    requestId: string;
    originalArgs: any;
    isError: boolean;
  };
  const error = response?.error as {
    error: Error;
    status: string;
  };

  useEffect(() => {
    if (response.isError && String(error.status) !== '404') {
      console.error(dedent`Request error: ${error.status}
      Endpoint name: ${response.endpointName}
      Request ID: ${response.requestId}
      Original args: ${JSON.stringify(response.originalArgs)}
      Full Error: ${JSON.stringify(error || {})}
      `);
      setQueryError({
        status: error.status,
        endpointName: response.endpointName,
        requestId: response.requestId,
        originalArgs: response.originalArgs,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  return queryResult;
};

export const useHandleMutationError = <
  T extends readonly [MutationTrigger<any>, UseMutationStateResult<any, any>],
>(
  mutationHook: () => T,
): T => {
  const res = mutationHook();
  const [, mutationStateResult] = res;
  const [, setMutationError] = useMutationError();
  const { error, isError, endpointName, originalArgs } = mutationStateResult;

  useEffect(() => {
    if (isError && String(error.status) !== '404') {
      console.error(dedent`Mutation error: ${error.status}
      Endpoint name: ${endpointName}
      Original args: ${JSON.stringify(originalArgs)}
      `);
      setMutationError({
        status: error.status,
        endpointName: endpointName,
        originalArgs: originalArgs,
      });
    }
  }, [endpointName, error, isError, originalArgs, setMutationError]);
  return res;
};
